import { getOwner } from '@ember/owner';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import type { IconFlagCode } from '@mylighthouse/prism-ember/types/components/prism/icon-flag';
import Countries from 'frontend-utils/utils/countries';
import hasFeatureToggle from 'lumen/helpers/has-feature-toggle';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  type CountryCodeWithDefault,
  DEFAULT_POS,
  type SelectInputOptionWithId,
} from 'lumen/utils/constants/pi-constant-values';

interface ParityDropdownsPosSelectionSignature {
  Args: {
    hasError: boolean;
    isLoading: boolean;
    onChange: (option: SelectInputOptionWithId<CountryCodeWithDefault>) => void;
    posValues: CountryCodeWithDefault[];
    selectedPos: CountryCodeWithDefault;
  };
}

export default class ParityDropdownsPosSelection extends Component<ParityDropdownsPosSelectionSignature> {
  @service declare intl: Services['intl'];

  private countries = new Countries(getOwner(this)!);

  @cached // Preserve reference for isEqual check in PowerSelect code
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get availablePos() {
    if (!this.hasMultiplePosValues) {
      return [this.defaultPos];
    }

    return [
      this.defaultPos,
      ...this.args.posValues.map((pos) => ({
        id: pos,
        label: this.countries.getName(pos),
        iconFlag: pos.toLowerCase() as IconFlagCode,
      })),
    ];
  }

  get defaultPos(): SelectInputOptionWithId<CountryCodeWithDefault> {
    return {
      id: DEFAULT_POS,
      label: this.intl.t('parity.channel_view'),
    };
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasMultiplePosValues() {
    return this.args.posValues.length > 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPosDropdownDisabled() {
    return (
      this.args.isLoading || !this.hasMultiplePosValues || this.args.hasError
    );
  }

  get posDisabledMessage(): string | undefined {
    if (this.args.hasError) {
      return this.intl.t('general.error_short');
    }

    if (this.showPOSUpsellCta) {
      return this.intl.t('parity.multi_pos.cta');
    }

    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedPosOption() {
    // Custom POS can't be displayed until data has been fetched
    if (this.args.isLoading) {
      return this.defaultPos;
    }

    return this.availablePos.find((pos) => pos.id === this.args.selectedPos);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showPOSUpsellCta() {
    return !this.args.isLoading && !this.hasMultiplePosValues;
  }

  <template>
    {{#if (hasFeatureToggle FEATURE_TOGGLES.PI_MULTI_POS)}}
      <PrismSelectInput
        @disabled={{this.isPosDropdownDisabled}}
        @disabledMessage={{this.posDisabledMessage}}
        @onChange={{@onChange}}
        @options={{this.availablePos}}
        @value={{this.selectedPosOption}}
      />
    {{/if}}
  </template>
}
